import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, BellRing, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const fortalezaLocal = (date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Fortaleza', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const newForm = () => ({
  title: '',
  body: '',
  starts_at: fortalezaLocal(new Date(Date.now() + 5 * 60 * 1000)),
  expires_at: fortalezaLocal(new Date(Date.now() + 3 * 60 * 60 * 1000)),
  roles: [],
  active: false,
});

const toForm = (notice) => ({
  title: notice.title,
  body: notice.body,
  starts_at: fortalezaLocal(new Date(notice.starts_at)),
  expires_at: fortalezaLocal(new Date(notice.expires_at)),
  roles: notice.roles,
  active: notice.active,
});

const toPayload = (form) => ({
  ...form,
  starts_at: new Date(`${form.starts_at}:00-03:00`).toISOString(),
  expires_at: new Date(`${form.expires_at}:00-03:00`).toISOString(),
});

const formatDate = (value) => value
  ? new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Fortaleza' })
  : '—';

const noticeStatus = (notice) => {
  if (notice.deleted_at) return 'Excluída';
  if (!notice.active) return 'Inativa';
  const now = Date.now();
  if (new Date(notice.expires_at).getTime() <= now) return 'Expirada';
  if (new Date(notice.starts_at).getTime() > now) return 'Agendada';
  return 'Ativa';
};

const errorText = (error) => error.response?.data?.detail || 'Não foi possível concluir a operação.';

export default function SystemNoticesPage() {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [roles, setRoles] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [viewRevision, setViewRevision] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [detailStats, setDetailStats] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(newForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/system-notices/admin`);
      setNotices(data.notices || []);
      setRoles(data.roles || {});
      setSelectedId((current) => current && data.notices.some((item) => item.id === current)
        ? current : data.notices[0]?.id || null);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!selectedId) {
      setRecipients([]);
      setDetailStats(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/system-notices/admin/${encodeURIComponent(selectedId)}/recipients`, {
      params: viewRevision ? { revision: viewRevision } : {},
    })
      .then(({ data }) => {
        if (!cancelled) {
          setRecipients(data.recipients || []);
          setDetailStats(data.stats || null);
        }
      })
      .catch((error) => { if (!cancelled) toast.error(errorText(error)); });
    return () => { cancelled = true; };
  }, [selectedId, viewRevision, notices]);

  const selected = notices.find((notice) => notice.id === selectedId);
  const displayedNotice = selected?.history?.find((item) => item.revision === viewRevision) || selected;

  const openCreate = () => {
    setEditingId(null);
    setForm(newForm());
    setShowForm(true);
  };

  const openEdit = (notice) => {
    setEditingId(notice.id);
    setForm(toForm(notice));
    setShowForm(true);
  };

  const toggleRole = (role) => {
    setForm((current) => ({
      ...current,
      roles: current.roles.includes(role)
        ? current.roles.filter((item) => item !== role)
        : [...current.roles, role],
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!form.roles.length) {
      toast.error('Escolha pelo menos um perfil destinatário.');
      return;
    }
    if (new Date(`${form.expires_at}:00-03:00`) <= new Date(`${form.starts_at}:00-03:00`)) {
      toast.error('A expiração deve ser posterior à ativação.');
      return;
    }
    setSaving(true);
    try {
      const payload = toPayload(form);
      const { data } = editingId
        ? await axios.put(`${API}/system-notices/admin/${encodeURIComponent(editingId)}`, payload)
        : await axios.post(`${API}/system-notices/admin`, payload);
      toast.success(editingId ? 'Notificação atualizada.' : 'Notificação criada.');
      setShowForm(false);
      await refresh();
      setSelectedId(data.id);
      setViewRevision(null);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (notice) => {
    if (!window.confirm(`Excluir a notificação "${notice.title}"? O histórico de ciência será preservado.`)) return;
    try {
      await axios.delete(`${API}/system-notices/admin/${encodeURIComponent(notice.id)}`);
      toast.success('Notificação excluída. O histórico foi preservado.');
      await refresh();
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  const stats = detailStats || selected?.stats || {};

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="icon" onClick={() => navigate('/')} aria-label="Voltar ao painel"><ArrowLeft className="h-4 w-4" /></Button>
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-emerald-900"><BellRing className="h-6 w-6" /> Notificações do sistema</h1>
              <p className="text-sm text-slate-600">Área do Programador · horários de Fortaleza</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refresh} disabled={loading}><RefreshCw className="mr-2 h-4 w-4" /> Atualizar</Button>
            <Button onClick={openCreate} className="bg-emerald-700 hover:bg-emerald-800"><Plus className="mr-2 h-4 w-4" /> Nova notificação</Button>
          </div>
        </div>

        {showForm && (
          <Card>
            <CardHeader><CardTitle>{editingId ? 'Editar notificação' : 'Nova notificação'}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={save} className="space-y-4">
                <div className="space-y-2"><Label htmlFor="notice-title">Título</Label><Input id="notice-title" value={form.title} maxLength={120} required onChange={(event) => setForm({ ...form, title: event.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="notice-body">Texto da notificação</Label><Textarea id="notice-body" value={form.body} maxLength={2000} required rows={5} onChange={(event) => setForm({ ...form, body: event.target.value })} /></div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label htmlFor="notice-start">Ativar em</Label><Input id="notice-start" type="datetime-local" value={form.starts_at} required onChange={(event) => setForm({ ...form, starts_at: event.target.value })} /></div>
                  <div className="space-y-2"><Label htmlFor="notice-end">Expirar em</Label><Input id="notice-end" type="datetime-local" value={form.expires_at} required onChange={(event) => setForm({ ...form, expires_at: event.target.value })} /></div>
                </div>
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">Perfis que receberão o aviso</legend>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Object.entries(roles).map(([role, label]) => (
                      <label key={role} className="flex items-center gap-2 rounded-lg border bg-white p-3 text-sm">
                        <Checkbox checked={form.roles.includes(role)} onCheckedChange={() => toggleRole(role)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <label className="flex items-center gap-3 rounded-lg bg-emerald-50 p-3 text-sm">
                  <Checkbox checked={form.active} onCheckedChange={(checked) => setForm({ ...form, active: checked === true })} />
                  Ativa (será exibida somente entre as datas definidas)
                </label>
                <p className="text-xs text-slate-600">Editar título, texto, período ou perfis cria uma nova versão. A ciência anterior fica no histórico e o aviso será apresentado novamente ao público da nova versão.</p>
                <div className="flex gap-2">
                  <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">{saving ? 'Salvando...' : 'Salvar notificação'}</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <Card>
            <CardHeader><CardTitle>Notificações</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {!loading && !notices.length && <p className="text-sm text-slate-500">Nenhuma notificação cadastrada.</p>}
              {notices.map((notice) => (
                <button key={notice.id} type="button" onClick={() => { setSelectedId(notice.id); setViewRevision(null); setDetailStats(null); setRecipients([]); }}
                  className={`w-full rounded-lg border p-3 text-left ${selectedId === notice.id ? 'border-emerald-600 bg-emerald-50' : 'bg-white hover:bg-slate-50'}`}>
                  <span className="block font-semibold text-slate-900">{notice.title}</span>
                  <span className="block text-xs text-slate-600">{noticeStatus(notice)} · versão {notice.revision}</span>
                  <span className="block text-xs text-slate-600">{notice.stats?.acknowledged_percent || 0}% deram ciência</span>
                </button>
              ))}
            </CardContent>
          </Card>

          {selected && (
            <div className="space-y-4">
              <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                  <div><CardTitle>{displayedNotice.title}</CardTitle><p className="mt-1 text-sm text-slate-600">{noticeStatus(selected)} · versão {viewRevision || selected.revision}</p></div>
                  {!selected.deleted_at && <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(selected)}><Pencil className="mr-1 h-4 w-4" /> Editar</Button>
                    <Button size="sm" variant="outline" className="text-red-700" onClick={() => remove(selected)}><Trash2 className="mr-1 h-4 w-4" /> Excluir</Button>
                  </div>}
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <p className="whitespace-pre-wrap text-slate-700">{displayedNotice.body}</p>
                  <p><strong>Ativação:</strong> {formatDate(displayedNotice.starts_at)} · <strong>Expiração:</strong> {formatDate(displayedNotice.expires_at)}</p>
                  <p><strong>Perfis:</strong> {displayedNotice.roles.map((role) => roles[role] || role).join(', ')}</p>
                  {selected.revision > 1 && (
                    <label className="flex items-center gap-2"><span className="font-medium">Acompanhar versão:</span>
                      <select className="rounded border bg-white p-2" value={viewRevision || selected.revision} onChange={(event) => { setViewRevision(Number(event.target.value)); setDetailStats(null); setRecipients([]); }}>
                        {Array.from({ length: selected.revision }, (_, index) => index + 1).reverse().map((revision) => <option key={revision} value={revision}>Versão {revision}</option>)}
                      </select>
                    </label>
                  )}
                </CardContent>
              </Card>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {[
                  ['Público', stats.eligible],
                  ['Receberam', stats.delivered],
                  ['Viram', stats.viewed],
                  ['Cientes', stats.acknowledged],
                  ['% de ciência', `${stats.acknowledged_percent || 0}%`],
                ].map(([label, value]) => (
                  <Card key={label}><CardContent className="p-4"><p className="text-xs text-slate-600">{label}</p><p className="text-2xl font-bold text-emerald-800">{value ?? 0}</p></CardContent></Card>
                ))}
              </div>

              <Card>
                <CardHeader><CardTitle>Acompanhamento por usuário</CardTitle><p className="text-sm text-slate-600">Público = usuários ativos hoje nos perfis selecionados. Recebeu = aviso enviado ao navegador; viu = popup exibido. Horários e percentuais da versão selecionada.</p></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead><tr className="border-b text-slate-600"><th className="p-2">Usuário</th><th className="p-2">Perfil</th><th className="p-2">Recebeu</th><th className="p-2">Viu</th><th className="p-2">Ciência</th></tr></thead>
                      <tbody>{recipients.map((item) => (
                        <tr key={item.user_id} className="border-b">
                          <td className="p-2"><span className="block font-medium">{item.name}</span><span className="text-xs text-slate-500">{item.email}</span></td>
                          <td className="p-2">{roles[item.role] || item.role || '—'}</td>
                          <td className="p-2">{formatDate(item.delivered_at)}</td>
                          <td className="p-2">{formatDate(item.viewed_at)}</td>
                          <td className="p-2">{formatDate(item.acknowledged_at)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                    {!recipients.length && <p className="py-4 text-sm text-slate-500">Nenhum usuário no público desta notificação.</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
